import { statType } from '../domain/statTypes'
import { tallyKey } from '../domain/derive'
import type { ID, StatTypeId } from '../domain/types'

export interface GridRow {
  id: ID
  name: string
}

/**
 * The grid, drawn once and used by both the scorer and everyone watching.
 *
 * The brief asks watchers to see "the same grid", and the cheapest way to be
 * sure of that is for there to be only one. The single difference is `onTap`:
 * without it the cells are static, which is also the honest rendering of a
 * device that is not allowed to write.
 */
export function StatGrid({
  rows,
  stats,
  counts,
  totals,
  onTap,
}: {
  rows: GridRow[]
  stats: StatTypeId[]
  counts: Map<string, number>
  totals: Map<StatTypeId, number>
  onTap?(playerId: ID, stat: StatTypeId): void
}) {
  const readOnly = onTap == null

  return (
    <div
      className={'grid' + (readOnly ? ' grid--readonly' : '')}
      style={
        {
          /*
           * Custom properties rather than grid-template-* directly: an inline
           * grid-template beats any stylesheet, and the phone layout needs to
           * replace it with two columns of stacked blocks. Passing the tracks
           * as variables leaves that decision where it belongs, in CSS.
           */
          '--grid-cols': `minmax(140px, 1.3fr) repeat(${stats.length}, 1fr)`,
          // Fixed header and totals rows; the players share what is left.
          '--grid-rows': `var(--colhead-h) repeat(${rows.length}, minmax(0, 1fr)) var(--totals-h)`,
        } as React.CSSProperties
      }
    >
      <div className="grid__corner">Player</div>
      {stats.map((id) => {
        const def = statType(id)
        return (
          <div key={id} className="grid__colhead" style={{ color: def.color }}>
            {def.short}
          </div>
        )
      })}

      {rows.map((player) => (
        <div key={player.id} className="grid__row">
          <div className="grid__name">{player.name}</div>
          <div className="grid__cells">
            {stats.map((id) => {
              const def = statType(id)
              const count = counts.get(tallyKey(player.id, id)) ?? 0
              const content = (
                <>
                  {/* Only shown on a phone, where the column headers are gone. */}
                  <span className="tapbtn__label">{def.short}</span>
                  <span className="tapbtn__count">{count}</span>
                </>
              )
              const className = 'tapbtn' + (count > 0 ? ' tapbtn--has' : '')
              const style = { '--stat': def.color } as React.CSSProperties

              return readOnly ? (
                <div key={id} className={className + ' tapbtn--static'} style={style} aria-label={`${player.name} ${def.label}, ${count}`}>
                  {content}
                </div>
              ) : (
                <button
                  key={id}
                  type="button"
                  className={className}
                  style={style}
                  onClick={() => onTap?.(player.id, id)}
                  aria-label={`${player.name} ${def.label}, currently ${count}`}
                >
                  {content}
                </button>
              )
            })}
          </div>
        </div>
      ))}

      <div className="grid__row grid__row--totals">
        <div className="grid__totalhead">TEAM</div>
        <div className="grid__cells">
          {stats.map((id) => (
            <div key={id} className="grid__total" style={{ color: statType(id).color }}>
              <span className="tapbtn__label">{statType(id).short}</span>
              {totals.get(id) ?? 0}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
